/**
 * Contratos da camada de IA.
 *
 * Regra de arquitetura (ADR-0005): a IA NUNCA escreve dados diretamente.
 * Produz sempre uma `AiAction` em JSON estruturado, que passa por um portao de
 * confirmacao antes de chegar a um repositorio. Isto torna cada decisao da IA
 * inspecionavel, reversivel e auditavel.
 *
 * Regra de arquitetura (ADR-0003): o fornecedor de IA e substituivel.
 * Nada fora de @flos/ai sabe que existe Gemini.
 */

/** Uma intencao que um plugin sabe tratar. Vai no manifesto. */
export interface IntentDescriptor {
  /** Identificador `<plugin>.<verbo>`, ex. 'finance.add_expense'. */
  name: string;
  /** Descricao em portugues — e isto que o modelo le para escolher. */
  description: string;
  /** Frases exemplo, em portugues, para desambiguar. */
  examples: string[];
  /** Esquema dos parametros que o modelo deve extrair. */
  parameters: JsonSchema;
  /** Escopo necessario. Se o usuário nao o tiver, a intencao nem e oferecida. */
  requires?: string;
  /**
   * Se true, a acao altera dados importantes e TEM de ser confirmada pelo
   * usuário antes de ser aplicada. Por padrão true — o seguro e confirmar.
   */
  confirm?: boolean;
}

/** Subconjunto de JSON Schema que os fornecedores suportam de forma fiavel. */
export interface JsonSchema {
  type: 'object' | 'string' | 'number' | 'integer' | 'boolean' | 'array';
  description?: string;
  properties?: Record<string, JsonSchema>;
  items?: JsonSchema;
  required?: string[];
  enum?: string[];
  /**
   * Alem dos formatos JSON Schema habituais, o projecto define dois:
   *  - `'money-cents'` — inteiro em centimos (ver `MONEY_FORMAT`)
   *  - `'date'`        — data ISO sem hora (ver `DATE_FORMAT`)
   * Sao convencoes NOSSAS: o adaptador tem de as remover antes de enviar o
   * esquema, porque os fornecedores rejeitam formatos que nao conhecem.
   */
  format?: string;
  nullable?: boolean;
}

/** Valor monetario inteiro em centimos. Convencao do projecto, nao do JSON Schema. */
export const MONEY_FORMAT = 'money-cents';
/** Data ISO-8601 sem componente horaria. Convencao do projecto. */
export const DATE_FORMAT = 'date';

/** O que o modelo devolve depois de interpretar o pedido do usuário. */
export interface AiAction<P = Record<string, unknown>> {
  /** Id unico desta interpretacao. Liga-se as entradas de auditoria. */
  traceId: string;
  intent: string;
  parameters: P;
  /** 0..1. Abaixo de `confidenceThreshold` pedimos confirmacao explicita. */
  confidence: number;
  /** Frase curta em portugues a explicar o que vai acontecer. Mostrada ao usuário. */
  summary: string;
  /** Registros que sustentam a resposta. Obrigatorio em perguntas sobre dados. */
  citations?: Citation[];
  /** Preenchido quando o modelo nao teve a certeza e precisa de perguntar. */
  clarification?: string;
}

/** Aponta para um registro concreto que sustenta uma afirmacao da IA. */
export interface Citation {
  entity: string;
  recordId: string;
  /** Texto curto do que este registro diz, ex. 'Continente — 43,20 EUR — 12/03'. */
  label: string;
}

export interface AiMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface StructuredRequest<T> {
  /** Instrucoes de sistema. Sempre em portugues. */
  system: string;
  messages: AiMessage[];
  /** Esquema que a resposta tem de cumprir. */
  schema: JsonSchema;
  /** Dica de custo/latencia. O adaptador escolhe o modelo concreto. */
  tier?: ModelTier;
  /** Cancelamento. */
  signal?: AbortSignal;
  /** Apenas para tipagem do retorno. */
  _phantom?: T;
}

export interface TextRequest {
  system: string;
  messages: AiMessage[];
  tier?: ModelTier;
  signal?: AbortSignal;
}

/**
 * Nivel de capacidade pedido, nao um modelo concreto.
 * Cada adaptador mapeia isto para os seus modelos.
 */
export type ModelTier =
  /** Classificacao, extracao simples. Barato e rapido. */
  | 'fast'
  /** Conversa, raciocinio sobre dados da familia. O caso comum. */
  | 'balanced'
  /** Relatorios, reconciliacao bancaria, analise de fraude. */
  | 'deep';

export interface AiUsage {
  inputTokens: number;
  outputTokens: number;
  /** Custo estimado em USD. Alimenta o painel de custos. */
  estimatedCostUsd: number;
  model: string;
  latencyMs: number;
}

export interface AiResult<T> {
  data: T;
  usage: AiUsage;
  /** Resposta em bruto, salva para depuracao quando o parsing falha. */
  raw?: string;
}

export interface OcrRequest {
  /** Imagem em base64, sem o prefixo data:. */
  imageBase64: string;
  mimeType: string;
  /** O que procurar, ex. 'comprovante de supermercado'. Melhora bastante a extracao. */
  hint?: string;
  signal?: AbortSignal;
}

/**
 * Recibo extraido de uma fotografia.
 *
 * IMPORTANTE — UNIDADE MONETARIA: todos os valores monetarios aqui sao
 * **inteiros em centimos**, como em todo o resto do sistema. 43,20 EUR e `4320`,
 * nao `43.2`. Quem implementar um adaptador tem de normalizar antes de devolver.
 * Deixar isto por dizer seria pedir um erro de fator 100 na despesa da mae.
 */
export interface ExtractedReceipt {
  merchant: string | null;
  /** ISO-8601 date. */
  date: string | null;
  /** Total em centimos inteiros. */
  total: number | null;
  currency: string | null;
  items: Array<{
    description: string;
    quantity: number | null;
    /** Preco unitario em centimos inteiros. */
    unitPrice: number | null;
    /** Total da linha em centimos inteiros. */
    total: number | null;
    /** Categoria sugerida, do vocabulario de categorias do plugin financas. */
    category: string | null;
  }>;
  /** 0..1 — quao confiante o modelo esta na leitura global. */
  confidence: number;
  /** Campos que o modelo nao conseguiu ler e precisam de revisao humana. */
  needsReview: string[];
}

/**
 * O contrato que qualquer fornecedor de IA tem de cumprir.
 * Implementacoes: GeminiProvider (defeito), e um MockProvider para testes.
 */
export interface AiProvider {
  readonly id: string;
  readonly displayName: string;
  /** Capacidades opcionais que este fornecedor suporta. */
  readonly capabilities: {
    structured: boolean;
    vision: boolean;
    audio: boolean;
  };
  /**
   * Se o fornecedor tem tudo o que precisa para funcionar (tipicamente uma
   * chave). A interface tem de responder a isto, senao a UI e obrigada a
   * estreitar o tipo para uma implementacao concreta — o que quebra a regra 7.
   */
  isConfigured?(): boolean;
  /** Configuracao em runtime, ex. a familia colou a sua propria chave. */
  configure?(config: Record<string, unknown>): void;
  generateStructured<T>(req: StructuredRequest<T>): Promise<AiResult<T>>;
  generateText(req: TextRequest): Promise<AiResult<string>>;
  /** Extrai dados de uma imagem. So se `capabilities.vision`. */
  extractFromImage?<T>(req: OcrRequest & { schema: JsonSchema }): Promise<AiResult<T>>;
  /** Transcreve audio. So se `capabilities.audio`. Caso contrario usamos a Web Speech API. */
  transcribe?(audio: Blob, signal?: AbortSignal): Promise<AiResult<string>>;
}

export type AiErrorKind =
  | 'network'
  | 'auth'
  | 'rate_limit'
  | 'invalid_response'
  | 'safety'
  | 'unsupported'
  /** O nosso pedido e que estava mal formado — erro nosso, nao do modelo. */
  | 'bad_request';

/** Contexto opcional de diagnostico. Nunca contem a chave nem dados pessoais. */
export interface AiErrorDetails {
  /**
   * Resposta em bruto do modelo, quando o parsing ou a validacao falharam.
   * Sem isto, um `invalid_response` e indepuravel: o unico lugar onde estava a
   * informacao util era precisamente o texto que se perdeu ao lancar.
   */
  raw?: string;
  /** Sugestao de espera vinda do cabecalho `Retry-After`, em milissegundos. */
  retryAfterMs?: number;
  /** Problemas de validacao encontrados na resposta. */
  issues?: string[];
}

export class AiError extends Error {
  readonly raw?: string;
  readonly retryAfterMs?: number;
  readonly issues?: string[];

  constructor(
    message: string,
    readonly kind: AiErrorKind,
    readonly retryable: boolean,
    // `Error` ja declara `cause` desde o ES2022, logo tem de ser `override`.
    override readonly cause?: unknown,
    details?: AiErrorDetails,
  ) {
    super(message);
    this.name = 'AiError';
    this.raw = details?.raw;
    this.retryAfterMs = details?.retryAfterMs;
    this.issues = details?.issues;
  }

  /** Mensagem apresentavel ao usuário, em portugues, sem jargao. */
  get userMessage(): string {
    switch (this.kind) {
      case 'network':
        return 'Nao consegui falar com o assistente. Verifique a ligacao a internet.';
      case 'auth':
        return 'O assistente nao esta configurado. Peca ajuda a quem administra a familia.';
      case 'rate_limit':
        return 'O assistente esta ocupado. Tente outra vez daqui a pouco.';
      case 'safety':
        return 'Nao consigo responder a esse pedido.';
      case 'unsupported':
        return 'Esta funcao ainda nao esta disponivel.';
      case 'bad_request':
        // Culpa nossa, nao dela. A mensagem nao a deve fazer sentir-se errada.
        return 'Houve um problema no assistente. Ja registamos a falha.';
      default:
        return 'Nao percebi a resposta do assistente. Pode repetir de outra forma?';
    }
  }
}
